/**
 * Enhanced Self Appraisal Redux Slice
 * 
 * Comprehensive state management for self-appraisal functionality including:
 * - OKRs (Objectives and Key Results) management
 * - Nomination data handling  
 * - API integration with selfAppraisalService
 * - Loading states for data fetching, saving, and submission
 * - Error handling and validation
 * - Async thunks for API operations
 * - Selectors for computed state values
 * 
 * Features:
 * ✅ Data Loading: Fetch self-appraisal data from API
 * ✅ Draft Saving: Save work in progress without final submission
 * ✅ Final Submission: Submit and freeze the appraisal
 * ✅ State Management: Comprehensive loading and error states
 * ✅ Validation: Client-side validation support
 * ✅ Selectors: Computed state values for components
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { SelfAppraisalResponse, Employee, NominationEntry } from '../services/selfAppraisalService';
import { selfAppraisalService } from '../services/selfAppraisalService';

/**
 * OKR (Objectives and Key Results) Data Interface
 * 
 * Represents individual objectives in the self-appraisal form
 */
interface OKRData {
  id: string;           // Unique identifier for the OKR
  objective: string;    // Description of the objective
  target: string;       // Target/goal to be achieved
  endYearResult: string; // Actual result achieved
  rating: number | null;       // Self-assessment rating (1-5 scale)
}

/**
 * Nomination Data Interface
 * 
 * Represents nomination selections for 360-degree feedback
 */
interface NominationData {
  nominationApprover: string; // Email/name of person who approves nominations
  nominationApproverEmail?: string; // Email of the nomination approver for tooltip
  superiors: Employee[];      // List of superior nominees
  dotted_superior: Employee[]; // List of dotted-line superior nominees
  subordinates: Employee[];   // List of subordinate nominees
  additional_subordinates: Employee[]; // List of additional subordinate nominees
  peers: Employee[];          // List of peer nominees
  stakeholders: Employee[];   // List of stakeholder nominees
}

/**
 * Self Appraisal State Interface
 * 
 * Defines the complete state structure for self-appraisal functionality
 */
interface SelfAppraisalState {
  // Core data
  okrs: OKRData[];           // Array of OKR objectives
  nominations: NominationData; // Nomination selections
  
  // API data
  apiData: SelfAppraisalResponse | null; // Raw API response data
  
  // Loading states
  isLoading: boolean;        // Data fetch loading state
  isSubmitting: boolean;     // Form submission state
  isSaving: boolean;         // Draft saving state
  
  // Status information
  isEditable: boolean;       // Whether form can be edited (not frozen)
  lastSavedAt: string | null; // ISO timestamp of last save
  lastSubmittedAt: string | null; // ISO timestamp of last submission
  // Monotonic counter to generate stable unique OKR IDs
  nextOkRId: number;
  
  // Error handling
  error: string | null;      // Error message if any
  validationErrors: string[]; // Array of validation error messages
}

// ===== ASYNC THUNKS =====

/**
 * Async thunk to fetch self appraisal data from API
 */
export const fetchSelfAppraisalData = createAsyncThunk(
  'selfAppraisal/fetchData',
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await selfAppraisalService.getSelfAppraisal(email);
      return response.data;
    } catch (error) {
      // The axios interceptor returns a plain object (ApiError) - extract a useful message if possible
      if (error && typeof error === 'object' && 'message' in error && typeof (error as { message?: unknown }).message === 'string') {
        return rejectWithValue((error as { message: string }).message);
      }

      return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch data');
    }
  }
);

/**
 * Async thunk to save draft
 */
export const saveDraftThunk = createAsyncThunk(
  'selfAppraisal/saveDraft',
  async (
    { email, id, okrs, cycle_type }: { 
      email: string; 
      id: number; 
      okrs: Array<{
        id: string;
        objective: string;
        target: string;
        endYearResult: string;
        rating: number | null;
      }>;
      cycle_type?: number;
    },
    { rejectWithValue }
  ) => {
    
      const response = await selfAppraisalService.saveDraft(email, id, okrs, cycle_type);
      if(response.status_code ==200 || response.status_code ==201){
      return { data: response.data,message:response.message! , timestamp: new Date().toISOString() };
      }else
      return rejectWithValue(response.message || 'Failed to save draft');
  }
);

/**
 * Async thunk to update nominations
 */
export const updateNominationsThunk = createAsyncThunk(
  'selfAppraisal/updateNominations',
  async (
    { email, subordinate, additional_subordinate, peer, stakeholder, superior, dotted_superior }: { 
      email: string; 
      subordinate: NominationEntry[];
      additional_subordinate: NominationEntry[];
      peer: NominationEntry[];
      stakeholder: NominationEntry[];
      superior: NominationEntry[];
      dotted_superior: NominationEntry[];
    },
    { rejectWithValue }
  ) => {
   
      const response = await selfAppraisalService.updateNominations({
        email,
        nominations: {
          peer,
          subordinate,
          additional_subordinate,
          superior,
          stakeholder,
          dotted_superior
        }
      });
       if(response.status_code ==200 || response.status_code ==201){
      return response.data;
       }else{
      return rejectWithValue(response.message || 'Failed to update nominations');
       }
  }
);

/**
 * Async thunk to submit nominations
 */
export const submitNominationThunk = createAsyncThunk(
  'selfAppraisal/submitNomination',
  async (
    { email, subordinate, additional_subordinate, peer, stakeholder, superior, dotted_superior }: { 
      email: string; 
      subordinate: NominationEntry[];
      additional_subordinate: NominationEntry[];
      peer: NominationEntry[];
      stakeholder: NominationEntry[];
      superior: NominationEntry[];
      dotted_superior: NominationEntry[];
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await selfAppraisalService.submitNomination({
        email,
        nominations: {
          peer,
          subordinate,
          additional_subordinate,
          superior,
          stakeholder,
          dotted_superior
        }
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to submit nominations');
    }
  }
);

/**
 * Async thunk to submit final appraisal
 */
export const submitAppraisalThunk = createAsyncThunk(
  'selfAppraisal/submitAppraisal',
  async (
    { email, id, okrs, cycle_type }: { 
      email: string; 
      id: number; 
      okrs: Array<{
        id: string;
        objective: string;
        target: string;
        endYearResult: string;
        rating: number | null;
      }>;
      cycle_type?: number;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await selfAppraisalService.submitFinalAppraisal(email, id, okrs, cycle_type);
      return response.data;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Failed to submit appraisal');
    }
  }
);

/**
 * Initial state with sample data
 * 
 * Provides default OKR entries and nomination data for development/demo purposes
 */
const initialState: SelfAppraisalState = {
  // Core data
  okrs: [
    { id: '1', objective: '', target: '', endYearResult: '', rating: null },
  ],
  nominations: {
    nominationApprover: '',
    superiors: [],
    dotted_superior: [],
    subordinates: [],
    additional_subordinates: [],
    peers: [],
    stakeholders: [],
  },
  
  // API data
  apiData: null,
  
  // Loading states
  isLoading: false,
  isSubmitting: false,
  isSaving: false,
  
  // Status information
  isEditable: true,
  lastSavedAt: null,
  lastSubmittedAt: null,
  nextOkRId: 2,
  
  // Error handling
  error: null,
  validationErrors: [],
};

/**
 * Self Appraisal slice with reducers for managing OKR and nomination data
 */
const selfAppraisalSlice = createSlice({
  name: 'selfAppraisal',
  initialState,
  reducers: {
    // ===== DATA LOADING ACTIONS =====
    
    /**
     * Start loading self appraisal data from API
     */
    loadDataStart: (state) => {
      state.isLoading = true;
      state.error = null;
      state.validationErrors = [];
    },

    /**
     * Successfully loaded self appraisal data from API
     * 
     * @param action - Contains API response data
     */
    loadDataSuccess: (state, action: PayloadAction<SelfAppraisalResponse>) => {
      state.isLoading = false;
      state.apiData = action.payload;
      state.isEditable = !action.payload.is_freezed;
      state.error = null;
      
      // Update nominations with API data
      state.apiData.immediate_manager = action.payload.immediate_manager;
    },

    /**
     * Failed to load self appraisal data from API
     * 
     * @param action - Contains error message
     */
    loadDataFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },

    // ===== OKR MANAGEMENT ACTIONS =====
    
    /**
     * Update a specific field in an OKR entry
     * 
     * @param action - Contains OKR ID, field name, and new value
     */
    updateOKR: (state, action: PayloadAction<{ id: string; field: keyof OKRData; value: string | number | null }>) => {
      const okr = state.okrs.find(item => item.id === action.payload.id);
      if (okr) {
        if (action.payload.field === 'rating') {
          const rawValue = action.payload.value;
          let nextValue: number | null;

          if (rawValue === null) {
            nextValue = null;
          } else if (typeof rawValue === 'string') {
            const trimmed = rawValue.trim();
            if (!trimmed) {
              nextValue = null;
            } else {
              const parsed = Number(trimmed);
              nextValue = Number.isNaN(parsed) ? null : parsed;
            }
          } else {
            nextValue = rawValue;
          }

          if (nextValue === null) {
            okr.rating = null;
          } else {
            const clamped = Math.min(5, Math.max(1, Math.round(nextValue)));
            okr.rating = clamped;
          }
        } else if (typeof action.payload.value === 'string') {
          okr[action.payload.field] = action.payload.value;
        }
      }
      state.error = null;
    },
    
    /**
     * Add a new empty OKR entry to the list
     */
    addOKR: (state) => {
      // Use a monotonic counter to avoid ID collisions when items are removed
      const newId = state.nextOkRId.toString();
      state.nextOkRId += 1;
      state.okrs.push({
        id: newId,
        objective: '',
        target: '',
        endYearResult: '',
        rating: null,
      });
    },
    
    /**
     * Remove an OKR entry by ID
     * 
     * @param action - Contains the ID of the OKR to remove
     */
    removeOKR: (state, action: PayloadAction<string>) => {
      state.okrs = state.okrs.filter(okr => okr.id !== action.payload);
    },

    /**
     * Load OKRs from API data (transform API format to component format)
     * 
     * @param action - Contains array of OKR data from API
     */
    loadOKRsFromApi: (state, action: PayloadAction<OKRData[]>) => {
      state.okrs = action.payload;
      // Update nextOkRId to be greater than any existing numeric OKR id to avoid collisions
      try {
        const numericIds = state.okrs
          .map(o => parseInt(o.id, 10))
          .filter(n => !isNaN(n));
        const maxId = numericIds.length > 0 ? Math.max(...numericIds) : 0;
        state.nextOkRId = maxId + 1;
      } catch {
        state.nextOkRId = state.okrs.length + 1;
      }
    },

    // ===== NOMINATION MANAGEMENT ACTIONS =====
    
    /**
     * Update nomination data
     * 
     * @param action - Contains field name and new value for nominations
     */
    updateNomination: (state, action: PayloadAction<{ field: keyof NominationData; value: string | Employee[] }>) => {
      (state.nominations[action.payload.field] as string | Employee[]) = action.payload.value;
      // Clear errors when user makes changes
      state.error = null;
    },

    // ===== DRAFT SAVING ACTIONS =====

    /**
     * Start saving draft
     */
    saveDraftStart: (state) => {
      state.isSaving = true;
      state.error = null;
    },

    /**
     * Successfully saved draft
     * 
     * @param action - Contains updated API response data
     */
    saveDraftSuccess: (state, action: PayloadAction<{ data: SelfAppraisalResponse; timestamp: string }>) => {
      state.isSaving = false;
      state.apiData = action.payload.data;
      state.lastSavedAt = action.payload.timestamp;
      state.error = null;
    },

    /**
     * Failed to save draft
     * 
     * @param action - Contains error message
     */
    saveDraftFailure: (state, action: PayloadAction<string>) => {
      state.isSaving = false;
      state.error = action.payload;
    },

    // ===== FINAL SUBMISSION ACTIONS =====
    
    /**
     * Start form submission process
     */
    submitStart: (state) => {
      state.isSubmitting = true;
      state.error = null;
      state.validationErrors = [];
    },
    
    /**
     * Handle successful form submission
     * 
     * @param action - Contains updated API response data
     */
    submitSuccess: (state, action: PayloadAction<SelfAppraisalResponse>) => {
      state.isSubmitting = false;
      state.apiData = action.payload;
      state.isEditable = false; // Frozen after submission
      state.error = null;
      state.lastSavedAt = new Date().toISOString();
    },
    
    /**
     * Handle failed form submission
     * 
     * @param action - Contains error message
     */
    submitFailure: (state, action: PayloadAction<string>) => {
      state.isSubmitting = false;
      state.error = action.payload;
    },

    // ===== ERROR HANDLING ACTIONS =====

    /**
     * Set validation errors
     * 
     * @param action - Contains array of validation error messages
     */
    setValidationErrors: (state, action: PayloadAction<string[]>) => {
      state.validationErrors = action.payload;
      if (action.payload.length > 0) {
        state.error = 'Please fix the validation errors before proceeding.';
      }
    },
    
    /**
     * Clear any existing error messages
     */
    clearError: (state) => {
      state.error = null;
      state.validationErrors = [];
    },

    // ===== UTILITY ACTIONS =====

    /**
     * Reset state to initial values
     */
    resetState: (state) => {
      Object.assign(state, initialState);
    },

    /**
     * Update last saved timestamp
     * 
     * @param action - Contains ISO timestamp string
     */
    updateLastSavedAt: (state, action: PayloadAction<string>) => {
      state.lastSavedAt = action.payload;
    },
  },
  
  // ===== EXTRA REDUCERS FOR ASYNC THUNKS =====
  extraReducers: (builder) => {
    // Fetch self appraisal data
    builder
      .addCase(fetchSelfAppraisalData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSelfAppraisalData.fulfilled, (state, action) => {
        state.isLoading = false;
        console.log('Redux: API data received:', action.payload);
        if (action.payload) {
          // Capture previous apiData to detect initial load BEFORE we overwrite it
          const previousApiData = state.apiData;

          // Always update apiData and editable flag from server
          state.apiData = action.payload;
          state.isEditable = !action.payload.is_freezed;
          state.apiData.immediate_manager = action.payload.immediate_manager;

          console.log('Redux: Setting apiData with ID:', action.payload.id);

          // Decide whether to replace local OKRs with server OKRs.
          // Avoid clobbering local unsaved edits: only replace when
          // - there was no previous apiData (initial load), or
          // - the server data is frozen (final submission), or
          // - the current local OKRs match the server OKRs (no local edits).
          try {
            const serverOkrs = (action.payload.okrs && action.payload.okrs.length > 0)
              ? selfAppraisalService.transformOKRsFromApiFormat(action.payload.okrs)
              : [{ id: '1', objective: '', target: '', endYearResult: '', rating: null }];

            const localOkrsApi = selfAppraisalService.transformOKRsToApiFormat(state.okrs || []);
            const serverOkrsApi = action.payload.okrs || [];

            const localMatchesServer = JSON.stringify(localOkrsApi) === JSON.stringify(serverOkrsApi);

            const shouldReplaceOkrs = !previousApiData || action.payload.is_freezed || localMatchesServer;

            if (shouldReplaceOkrs) {
              state.okrs = serverOkrs;

              // Update nextOkRId to avoid collisions
              try {
                const numericIds = state.okrs
                  .map(o => parseInt(o.id as unknown as string, 10))
                  .filter(n => !isNaN(n));
                const maxId = numericIds.length > 0 ? Math.max(...numericIds) : 0;
                state.nextOkRId = maxId + 1;
              } catch {
                state.nextOkRId = state.okrs.length + 1;
              }
            } else {
              // Keep local OKRs to avoid losing user edits
              console.log('Redux: Skipping OKR overwrite to preserve local edits');
            }
          } catch (err) {
            // Fallback behaviour: if any error occurs during transformation, fall back to replacing OKRs
            console.error('Error while merging OKRs from server, falling back to server OKRs', err);
            if (action.payload.okrs && action.payload.okrs.length > 0) {
              state.okrs = selfAppraisalService.transformOKRsFromApiFormat(action.payload.okrs);
            } else {
              state.okrs = [{ id: '1', objective: '', target: '', endYearResult: '', rating: null }];
            }
          }
        }
      })
      .addCase(fetchSelfAppraisalData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // Save draft
      .addCase(saveDraftThunk.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(saveDraftThunk.fulfilled, (state, action) => {
        state.isSaving = false;
        if (action.payload && action.payload.data) {
          const newData = action.payload.data as Partial<SelfAppraisalResponse>;

          // Merge returned partial data into existing apiData to avoid losing fields
          const merged: SelfAppraisalResponse = Object.assign({}, state.apiData || {}, newData) as SelfAppraisalResponse;
          state.apiData = merged;

          // Update editability based on merged data
          state.isEditable = !(merged.is_freezed ?? false);

          // If server returned OKRs, transform and replace; otherwise preserve local OKRs
          if (newData.okrs && newData.okrs.length > 0) {
            state.okrs = selfAppraisalService.transformOKRsFromApiFormat(newData.okrs as SelfAppraisalResponse['okrs']);
            // update nextOkRId safely
            try {
              const numericIds = state.okrs
                .map(o => parseInt(o.id as unknown as string, 10))
                .filter(n => !isNaN(n));
              const maxId = numericIds.length > 0 ? Math.max(...numericIds) : 0;
              state.nextOkRId = maxId + 1;
            } catch {
              state.nextOkRId = state.okrs.length + 1;
            }
          }
          state.lastSavedAt = action.payload.timestamp;
        }
      })
      .addCase(saveDraftThunk.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      })

      // Update nominations
      .addCase(updateNominationsThunk.pending, (state) => {
        state.isSaving = true;
        state.error = null;
      })
      .addCase(updateNominationsThunk.fulfilled, (state, action) => {
        state.isSaving = false;
        if (action.payload) {
          state.lastSavedAt = new Date().toISOString();
        }
      })
      .addCase(updateNominationsThunk.rejected, (state, action) => {
        state.isSaving = false;
        state.error = action.payload as string;
      })

      // Submit nominations
      .addCase(submitNominationThunk.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(submitNominationThunk.fulfilled, (state, action) => {
        state.isSubmitting = false;
        if (action.payload) {
          state.lastSubmittedAt = new Date().toISOString();
        }
      })
      .addCase(submitNominationThunk.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload as string;
      })
      
      // Submit appraisal
      .addCase(submitAppraisalThunk.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
        state.validationErrors = [];
      })
      .addCase(submitAppraisalThunk.fulfilled, (state, action) => {
        state.isSubmitting = false;
        if (action.payload) {
          state.apiData = action.payload;
          state.isEditable = false;
          state.lastSavedAt = new Date().toISOString();
        }
      })
      .addCase(submitAppraisalThunk.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload as string;
      });
  },
});

// Export action creators for use in components
export const {
  // Data loading actions
  loadDataStart,
  loadDataSuccess,
  loadDataFailure,
  
  // OKR management actions
  updateOKR,
  addOKR,
  removeOKR,
  loadOKRsFromApi,
  
  // Nomination management actions
  updateNomination,
  
  // Draft saving actions
  saveDraftStart,
  saveDraftSuccess,
  saveDraftFailure,
  
  // Final submission actions
  submitStart,
  submitSuccess,
  submitFailure,
  
  // Error handling actions
  setValidationErrors,
  clearError,
  
  // Utility actions
  resetState,
  updateLastSavedAt,
} = selfAppraisalSlice.actions;

// ===== SELECTORS =====

/**
 * Selector functions for accessing specific parts of the state
 */
export const selfAppraisalSelectors = {
  // Basic state selectors
  selectOKRs: (state: { selfAppraisal: SelfAppraisalState }) => state.selfAppraisal.okrs,
  selectNominations: (state: { selfAppraisal: SelfAppraisalState }) => state.selfAppraisal.nominations,
  selectApiData: (state: { selfAppraisal: SelfAppraisalState }) => state.selfAppraisal.apiData,
  
  // Loading state selectors
  selectIsLoading: (state: { selfAppraisal: SelfAppraisalState }) => state.selfAppraisal.isLoading,
  selectIsSubmitting: (state: { selfAppraisal: SelfAppraisalState }) => state.selfAppraisal.isSubmitting,
  selectIsSaving: (state: { selfAppraisal: SelfAppraisalState }) => state.selfAppraisal.isSaving,
  
  // Status selectors
  selectIsEditable: (state: { selfAppraisal: SelfAppraisalState }) => state.selfAppraisal.isEditable,
  selectLastSavedAt: (state: { selfAppraisal: SelfAppraisalState }) => state.selfAppraisal.lastSavedAt,
  
  // Error selectors
  selectError: (state: { selfAppraisal: SelfAppraisalState }) => state.selfAppraisal.error,
  selectValidationErrors: (state: { selfAppraisal: SelfAppraisalState }) => state.selfAppraisal.validationErrors,
  selectHasErrors: (state: { selfAppraisal: SelfAppraisalState }) => 
    !!state.selfAppraisal.error || state.selfAppraisal.validationErrors.length > 0,
  
  // Computed selectors
  selectCanEdit: (state: { selfAppraisal: SelfAppraisalState }) => 
    state.selfAppraisal.isEditable && !state.selfAppraisal.isSubmitting,
  selectIsProcessing: (state: { selfAppraisal: SelfAppraisalState }) => 
    state.selfAppraisal.isLoading || state.selfAppraisal.isSubmitting || state.selfAppraisal.isSaving,
  selectAppraisalStatus: (state: { selfAppraisal: SelfAppraisalState }) => {
    const { apiData } = state.selfAppraisal;
    if (!apiData) return 'unknown';
    return apiData.is_freezed ? 'submitted' : 'draft';
  },
};

// Export reducer for store configuration
export default selfAppraisalSlice.reducer;
